/**
 * DropPartitionFormBody — partition name + per-engine flag toggles for
 * `drop-partition`. pgsql adds CONCURRENTLY; oracle adds UPDATE GLOBAL INDEXES.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice E1.
 */
import { useState } from 'react';

import { Button } from '../../../../../components/ui/button';
import { Checkbox } from '../../../../../components/ui/checkbox';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import type { QualifiedName } from '../../../../sql-grammar/model';
import {
  commitDropPartition,
  isDropPartitionConcurrentlySupported,
  isDropPartitionUpdateGlobalIndexesSupported,
  validateDropPartitionForEngine,
} from '../../edit/edits/dropPartition';
import type { VqeDdlEditContext } from '../../edit/editContext';
import { FormEngineChip } from './FormEngineChip';

export interface DropPartitionFormBodyProps {
  readonly editContext: VqeDdlEditContext;
  readonly testId: string;
  readonly table: QualifiedName | null;
  readonly onCommitted?: () => void;
  readonly onCancel?: () => void;
}

export function DropPartitionFormBody(props: DropPartitionFormBodyProps) {
  const { editContext, testId, onCommitted, onCancel, table } = props;
  const engine = editContext.service.engineId;
  const showConcurrently = isDropPartitionConcurrentlySupported(engine);
  const showUpdateGlobalIndexes = isDropPartitionUpdateGlobalIndexesSupported(engine);
  const [partitionName, setPartitionName] = useState('');
  const [concurrently, setConcurrently] = useState(false);
  const [updateGlobalIndexes, setUpdateGlobalIndexes] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function handleSubmit() {
    setError(null);
    if (!table) {
      setError('Could not resolve the target table from the parked statement.');
      return;
    }
    const args = {
      table,
      partitionName: partitionName.trim(),
      pgsqlConcurrently: showConcurrently && concurrently ? true : undefined,
      oracleUpdateGlobalIndexes: showUpdateGlobalIndexes && updateGlobalIndexes ? true : undefined,
    };
    const validation = validateDropPartitionForEngine(engine, args);
    if (validation !== null) { setError(validation); return; }
    setPending(true);
    try {
      await commitDropPartition(editContext, args);
      onCommitted?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPending(false);
    }
  }

  return (
    <div data-testid={testId} className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold">
          {engine === 'postgres' ? 'Detach partition' : 'Drop partition'}
        </div>
        <FormEngineChip engine={engine} />
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor={`${testId}-name`} className="text-xs">
          Partition name <span className="text-destructive">*</span>
        </Label>
        <Input
          id={`${testId}-name`}
          data-testid={`${testId}-name`}
          value={partitionName}
          onChange={(e) => setPartitionName(e.target.value)}
          placeholder="p2024"
          className="h-8 font-mono text-xs"
        />
      </div>

      {showConcurrently ? (
        <label className="flex items-center gap-2 text-xs">
          <Checkbox
            checked={concurrently}
            onCheckedChange={(checked) => setConcurrently(checked === true)}
            data-testid={`${testId}-concurrently`}
          />
          <span>CONCURRENTLY (online detach, no AccessExclusiveLock)</span>
        </label>
      ) : null}

      {showUpdateGlobalIndexes ? (
        <label className="flex items-center gap-2 text-xs">
          <Checkbox
            checked={updateGlobalIndexes}
            onCheckedChange={(checked) => setUpdateGlobalIndexes(checked === true)}
            data-testid={`${testId}-update-global-indexes`}
          />
          <span>UPDATE GLOBAL INDEXES (rebuild instead of invalidate)</span>
        </label>
      ) : null}

      {error ? (
        <div
          className="rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1 text-xs text-destructive"
          data-testid={`${testId}-error`}
        >
          {error}
        </div>
      ) : null}

      <div className="sticky bottom-0 -mx-4 -mb-4 flex items-center justify-end gap-2 border-t bg-popover px-4 py-2">
        <Button type="button" variant="ghost" size="sm" onClick={() => onCancel?.()} data-testid={`${testId}-cancel`}>Cancel</Button>
        <Button
          type="button"
          size="sm"
          variant="destructive"
          disabled={pending}
          onClick={() => void handleSubmit()}
          data-testid={`${testId}-submit`}
        >
          {pending ? 'Working…' : engine === 'postgres' ? 'Detach' : 'Drop'}
        </Button>
      </div>
    </div>
  );
}
